import { better, olderGeneration, worse, youngerGeneration } from '@/assets/messages';
import ToggledBarChart, { BarChartData } from '@/components/bar-chart/toggled';
import {
  OnStepEnterExitArgs,
  ScrollGraphic,
  ScrollRoot,
  ScrollStep,
  ScrollSteps,
} from '@/components/microstory/scroll';
import { StoryText } from '@/components/microstory/text';
import { useNumberFormatting } from '@/utils/use-number-formatting';
import { t, Trans } from '@lingui/macro';
import is from '@sindresorhus/is';
import { useState } from 'react';
import {
  AnnotatedType,
  PrimaryAnnotatedType as BasePrimaryAnnotatedType,
  SecondaryAnnotatedType as BaseSecondaryAnnotatedType,
} from '@/components/annotated-type';
import { styled } from '@/styles/stitches.config';
import { useLingui } from '@lingui/react';

enum AgeGroup {
  younger = 'younger',
  older = 'older',
}

type FutureFortunesChapter1Props = {
  data: FutureFortunesData;
};

const PrimaryAnnotatedType = styled(BasePrimaryAnnotatedType, {
  color: '$white',
});

const SecondaryAnnotatedType = styled(BaseSecondaryAnnotatedType, {
  color: '$white',
});

export function FutureFortunesChapter1(props: FutureFortunesChapter1Props) {
  const { data } = props;

  const formatPercentage = useNumberFormatting('percent');
  const { i18n } = useLingui();

  const allCountriesChartLabel = t({
    id: 'future-fortunes.chapter-1.chart-label',
    message:
      '% who think children in their country will become economically better off than their parents',
  });

  const hicCountriesChartLabel = t({
    id: 'future-fortunes.chapter-1.chart-label-hic',
    message:
      "% in high-income countries who think today's children will be economically better off than their parents",
  });

  const lmicCountriesChartLabel = t({
    id: 'future-fortunes.chapter-1.chart-label-lmic',
    message:
      "% in low and lower-middle income countries who think today's children will be economically better off than their parents",
  });

  const [isChartVisible, setIsChartVisible] = useState(false);
  const [toggledId, setChartToggledId] = useState(AgeGroup.younger);
  const [chartLabel, setChartLabel] = useState(allCountriesChartLabel);
  const [chartData, setChartData] = useState<BarChartData[]>([
    {
      id: AgeGroup.younger,
      label: youngerGeneration(i18n),
      data: [
        {
          value: data.economic_prospects.younger.better,
          label: better(i18n),
        },
      ],
    },
  ]);

  const handleStepEnter = async (
    evt: OnStepEnterExitArgs<
      (
        | {
            key: 'data';
            payload: BarChartData[];
          }
        | { key: 'id'; payload: AgeGroup }
        | { key: 'label'; payload: string }
      )[]
    >
  ) => {
    setIsChartVisible(true);

    if (is.array(evt.data)) {
      evt.data.forEach((data) => {
        if (data?.key === 'data') setChartData(data.payload);
        if (data?.key === 'id') setChartToggledId(data.payload);
        if (data?.key === 'label') setChartLabel(data.payload);
      });
    }
  };

  return (
    <ScrollRoot>
      <ScrollGraphic>
        <ToggledBarChart
          label={chartLabel}
          visible={isChartVisible}
          data={chartData}
          formatValue={formatPercentage}
          initialId={toggledId}
        />
      </ScrollGraphic>

      <ScrollSteps onStepEnter={handleStepEnter}>
        <ScrollStep
          data={[
            {
              key: 'data',
              payload: [
                {
                  id: AgeGroup.younger,
                  label: youngerGeneration(i18n),
                  data: [
                    {
                      value: data.economic_prospects.younger.better,
                      label: better(i18n),
                    },
                  ],
                },
              ],
            },
            {
              key: 'id',
              payload: AgeGroup.younger,
            },
            {
              key: 'label',
              payload: allCountriesChartLabel,
            },
          ]}
        >
          <StoryText>
            <Trans id="future-fortunes.chapter-1.text-1">
              On average, {formatPercentage(data.economic_prospects.younger.better)} of young people
              say they think children in their country will be{' '}
              <PrimaryAnnotatedType>economically better off</PrimaryAnnotatedType> than their
              parents when they grow up.
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep
          data={[
            {
              key: 'data',
              payload: [
                {
                  id: AgeGroup.younger,
                  label: youngerGeneration(i18n),
                  data: [
                    {
                      value: data.economic_prospects.younger.better,
                      label: better(i18n),
                    },
                    {
                      value: data.economic_prospects.younger.worse,
                      label: worse(i18n),
                    },
                  ],
                },
              ],
            },
            {
              key: 'id',
              payload: AgeGroup.younger,
            },
            {
              key: 'label',
              payload: allCountriesChartLabel,
            },
          ]}
        >
          <StoryText>
            <Trans id="future-fortunes.chapter-1.text-2">
              Just {formatPercentage(data.economic_prospects.younger.worse)} answered that children
              will be <SecondaryAnnotatedType>worse off</SecondaryAnnotatedType> than their parents.
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep
          data={[
            {
              key: 'data',
              payload: [
                {
                  id: AgeGroup.younger,
                  label: youngerGeneration(i18n),
                  data: [
                    {
                      value: data.economic_prospects.younger.better,
                      label: better(i18n),
                    },
                    {
                      value: data.economic_prospects.younger.worse,
                      label: worse(i18n),
                    },
                  ],
                },
                {
                  id: AgeGroup.older,
                  label: olderGeneration(i18n),
                  data: [
                    {
                      value: data.economic_prospects.older.better,
                      label: better(i18n),
                    },
                    {
                      value: data.economic_prospects.older.worse,
                      label: worse(i18n),
                    },
                  ],
                },
              ],
            },
            {
              key: 'id',
              payload: AgeGroup.younger,
            },
            {
              key: 'label',
              payload: allCountriesChartLabel,
            },
          ]}
        >
          <StoryText>
            <Trans id="future-fortunes.chapter-1.text-3">
              Again, young people are <AnnotatedType>more positive</AnnotatedType> than older
              generations.
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep
          data={[
            {
              key: 'data',
              payload: [
                {
                  id: AgeGroup.younger,
                  label: youngerGeneration(i18n),
                  data: [
                    {
                      value: data.economic_prospects.younger.better,
                      label: better(i18n),
                    },
                    {
                      value: data.economic_prospects.younger.worse,
                      label: worse(i18n),
                    },
                  ],
                },
                {
                  id: AgeGroup.older,
                  label: olderGeneration(i18n),
                  data: [
                    {
                      value: data.economic_prospects.older.better,
                      label: better(i18n),
                    },
                    {
                      value: data.economic_prospects.older.worse,
                      label: worse(i18n),
                    },
                  ],
                },
              ],
            },
            {
              key: 'id',
              payload: AgeGroup.older,
            },
            {
              key: 'label',
              payload: allCountriesChartLabel,
            },
          ]}
        >
          <StoryText>
            <Trans id="future-fortunes.chapter-1.text-4">
              On average, people over 40 are more likely to say that today’s children will end up{' '}
              <SecondaryAnnotatedType>economically worse off</SecondaryAnnotatedType> than their
              parents.
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep
          data={[
            {
              key: 'data',
              payload: [
                {
                  id: AgeGroup.younger,
                  label: youngerGeneration(i18n),
                  data: [
                    {
                      value: data.economic_prospects.younger.better,
                      label: better(i18n),
                    },
                    {
                      value: data.economic_prospects.younger.worse,
                      label: worse(i18n),
                    },
                  ],
                },
                {
                  id: AgeGroup.older,
                  label: olderGeneration(i18n),
                  data: [
                    {
                      value: data.economic_prospects.older.better,
                      label: better(i18n),
                    },
                    {
                      value: data.economic_prospects.older.worse,
                      label: worse(i18n),
                    },
                  ],
                },
              ],
            },
            {
              key: 'id',
              payload: AgeGroup.younger,
            },
            {
              key: 'label',
              payload: allCountriesChartLabel,
            },
          ]}
        >
          <StoryText>
            <Trans id="future-fortunes.chapter-1.text-5">
              The <PrimaryAnnotatedType>upbeat response</PrimaryAnnotatedType> from young people is
              a powerful marker of faith in progress.
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep>
          <StoryText>
            <Trans id="future-fortunes.chapter-1.text-6">
              Comparing one's living standards to those of one's parents is one of the most
              important benchmarks people use to assess success in life.
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep
          data={[
            {
              key: 'data',
              payload: [
                {
                  id: AgeGroup.younger,
                  label: youngerGeneration(i18n),
                  data: [
                    {
                      value: data.economic_prospects.younger.better,
                      label: better(i18n),
                    },
                    {
                      value: data.economic_prospects.younger.worse,
                      label: worse(i18n),
                    },
                  ],
                },
                {
                  id: AgeGroup.older,
                  label: olderGeneration(i18n),
                  data: [
                    {
                      value: data.economic_prospects.older.better,
                      label: better(i18n),
                    },
                    {
                      value: data.economic_prospects.older.worse,
                      label: worse(i18n),
                    },
                  ],
                },
              ],
            },
            {
              key: 'id',
              payload: AgeGroup.younger,
            },
            {
              key: 'label',
              payload: allCountriesChartLabel,
            },
          ]}
        >
          <StoryText>
            <Trans id="future-fortunes.chapter-1.text-7">
              By this measure, young people feel{' '}
              <PrimaryAnnotatedType>pretty good</PrimaryAnnotatedType> about the prospects of their
              generation…
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep>
          <StoryText>
            <Trans id="future-fortunes.chapter-1.text-8">… but not so fast!</Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep
          data={[
            {
              key: 'data',
              payload: [
                {
                  id: AgeGroup.younger,
                  label: youngerGeneration(i18n),
                  data: [
                    {
                      value: data.economic_prospects_hic.younger.better,
                      label: better(i18n),
                    },
                    {
                      value: data.economic_prospects_hic.younger.worse,
                      label: worse(i18n),
                    },
                  ],
                },
                {
                  id: AgeGroup.older,
                  label: olderGeneration(i18n),
                  data: [
                    {
                      value: data.economic_prospects_hic.older.better,
                      label: better(i18n),
                    },
                    {
                      value: data.economic_prospects_hic.older.worse,
                      label: worse(i18n),
                    },
                  ],
                },
              ],
            },
            {
              key: 'id',
              payload: AgeGroup.younger,
            },
            {
              key: 'label',
              payload: hicCountriesChartLabel,
            },
          ]}
        >
          <StoryText>
            <Trans id="future-fortunes.chapter-1.text-9">
              This belief in progress among the young is{' '}
              <SecondaryAnnotatedType>absent</SecondaryAnnotatedType> from most rich countries.
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep>
          <StoryText>
            <Trans id="future-fortunes.chapter-1.text-10">
              There, young people are twice as likely to say they expect today's children will end
              up <SecondaryAnnotatedType>economically worse off</SecondaryAnnotatedType> than their
              parents, versus <PrimaryAnnotatedType>better off.</PrimaryAnnotatedType>
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep
          data={[
            {
              key: 'data',
              payload: [
                {
                  id: AgeGroup.younger,
                  label: youngerGeneration(i18n),
                  data: [
                    {
                      value: data.economic_prospects_lmic.younger.better,
                      label: better(i18n),
                    },
                    {
                      value: data.economic_prospects_lmic.younger.worse,
                      label: worse(i18n),
                    },
                  ],
                },
                {
                  id: AgeGroup.older,
                  label: olderGeneration(i18n),
                  data: [
                    {
                      value: data.economic_prospects_lmic.older.better,
                      label: better(i18n),
                    },
                    {
                      value: data.economic_prospects_lmic.older.worse,
                      label: worse(i18n),
                    },
                  ],
                },
              ],
            },
            {
              key: 'id',
              payload: AgeGroup.younger,
            },
            {
              key: 'label',
              payload: lmicCountriesChartLabel,
            },
          ]}
        >
          <StoryText>
            <Trans id="future-fortunes.chapter-1.text-11">
              Most young people in rich countries have resources and opportunities that are the envy
              of young people in developing countries.
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep
          data={[
            {
              key: 'data',
              payload: [
                {
                  id: AgeGroup.younger,
                  label: youngerGeneration(i18n),
                  data: [
                    {
                      value: data.economic_prospects_hic.younger.better,
                      label: better(i18n),
                    },
                    {
                      value: data.economic_prospects_hic.younger.worse,
                      label: worse(i18n),
                    },
                  ],
                },
                {
                  id: AgeGroup.older,
                  label: olderGeneration(i18n),
                  data: [
                    {
                      value: data.economic_prospects_hic.older.better,
                      label: better(i18n),
                    },
                    {
                      value: data.economic_prospects_hic.older.worse,
                      label: worse(i18n),
                    },
                  ],
                },
              ],
            },
            {
              key: 'id',
              payload: AgeGroup.younger,
            },
            {
              key: 'label',
              payload: hicCountriesChartLabel,
            },
          ]}
        >
          <StoryText>
            <Trans id="future-fortunes.chapter-1.text-12">
              Yet as they look to their futures, young people in rich countries are{' '}
              <SecondaryAnnotatedType>weighed down by economic unease.</SecondaryAnnotatedType>
            </Trans>
          </StoryText>
        </ScrollStep>
      </ScrollSteps>
    </ScrollRoot>
  );
}
